import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Content, PageWithHeader } from '@backstage/core-components';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { Button, makeStyles, Theme } from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';
import { DevxBreadCrumb } from '../common/BreadcrumbsNav/DevxBreadCrumb';
import BackButton from '../utils/backButton';
import { AgentCard, AgentCardProps } from './components/AgentCard';
import { AgentModal } from './components/AgentModal';
import { FilterBar } from './components/FilterBar';
import { AddAssistant } from './components/AddAssistant';
import { ReviewTable } from './components/ReviewTable';
import SearchIcon from './components/SearchIcon';
import {
  AiAssistantsCatalogApi,
  aiAssistantsCatalogApiRef,
  AssistantRecord,
} from '../../apis/aiAssistantsCatalogApi';
import {
  NotificationApi,
  notificationApiRef,
} from '../../apis/notificationApi';

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    width: '100%',
  },
  container: {
    width: '100%',
    maxWidth: 1200,
    margin: '0 auto',
    padding: 16,
  },
  searchBarWrapper: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    margin: '16px 0 20px',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  searchBarLarge: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${theme.palette.border}`,
    borderRadius: 8,
    background: theme.palette.background.paper,
  },
  searchInputLarge: {
    flex: 1,
    border: 0,
    outline: 'none',
    background: 'transparent',
    padding: '10px 12px',
    fontSize: 14,
    color: theme.palette.text.secondary,
  },
  searchButton: {
    padding: '0 12px',
    color: theme.palette.text.tertiary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      display: 'block',
    },
  },
  addAgentBtn: {
    display: 'flex',
    alignItems: 'center',
    textTransform: 'none',
    minWidth: 'auto',
    padding: '10px 16px',
    fontSize: 12,
    fontWeight: 600,
    fontFamily: theme.typography.fontFamily,
    lineHeight: '20px',
    borderRadius: 4,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark || theme.palette.primary.main,
      boxShadow: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'center',
    },
  },
  agentsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: 16,
  },
  emptyState: {
    border: `1px dashed ${theme.palette.border}`,
    borderRadius: 8,
    padding: 20,
    textAlign: 'center',
    color: theme.palette.text.tertiary,
    background: theme.palette.background.paper,
  },
  buttonsWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
    gap: 16,
  },
  rightButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  guideButton: {
    color: '#003f2d',
    fontSize: 14,
    textTransform: 'uppercase',
    padding: '6px 16px',
    display: 'flex',
    alignItems: 'center',
    minWidth: 'auto',
    borderRadius: 4,
    textAlign: 'right',
    '&:hover': {
      backgroundColor: 'rgba(46, 119, 208, 0.04)',
    },
    '& .MuiButton-label': {
      display: 'flex',
      alignItems: 'center',
    },
    '& svg': {
      marginRight: 5,
    },
  },
}));

const DEFAULT_BADGE_COLOR = '#059669';

const normalizeStatus = (value?: string): 'Approved' | 'Under Review' => {
  const normalized = (value || '').toLowerCase();
  if (normalized === 'approved') {
    return 'Approved';
  }
  return 'Under Review';
};

const formatDate = (value?: string) => {
  if (!value) {
    return 'Recently Updated';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Recently Updated';
  }
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  });
};

const extractDataNode = (input: any): any => {
  if (input == null) {
    return input;
  }
  if (Array.isArray(input)) {
    return input;
  }
  if (Array.isArray(input?.data)) {
    return input.data;
  }
  if (input?.data && typeof input.data === 'object') {
    return extractDataNode(input.data);
  }
  if (Array.isArray(input?.items)) {
    return input.items;
  }
  if (Array.isArray(input?.results)) {
    return input.results;
  }
  if (input?.result && typeof input.result === 'object') {
    return extractDataNode(input.result);
  }
  return input;
};

const extractAssistantList = (input: any): AssistantRecord[] => {
  const data = extractDataNode(input);
  if (Array.isArray(data)) {
    return data as AssistantRecord[];
  }
  if (data && typeof data === 'object' && (data.id || data.name)) {
    return [data as AssistantRecord];
  }
  return [];
};

const extractAssistantId = (input: any): string | undefined => {
  const data = extractDataNode(input);
  if (typeof data?.id === 'string') {
    return data.id;
  }
  if (typeof input?.id === 'string') {
    return input.id;
  }
  return undefined;
};

const extractBookmarkIds = (input: any): Set<string> => {
  const data = extractDataNode(input);
  if (!Array.isArray(data)) {
    return new Set<string>();
  }

  const ids = data
    .map(item => {
      if (typeof item === 'string') {
        return item;
      }
      return item?.assistantId || item?.assistantid || item?.id;
    })
    .filter((value): value is string => typeof value === 'string' && !!value);

  return new Set(ids);
};

const normalizeTextValue = (value?: string) => {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
};

const normalizeArrayValues = (values?: string[]) =>
  (values || []).map(value => value?.trim()).filter((value): value is string => !!value);

const normalizeEmailValue = (value?: string) =>
  (value || '').trim().toLowerCase();

const isCopilotCategory = (value?: string) => {
  const normalized = normalizeTextValue(value)?.toLowerCase();
  return normalized === 'copilot' || normalized === 'co-pilot';
};

const ASSISTANT_TYPE_VALUES = [
  'Personal Agent',
  'Team Agent',
  'Enterprise Agent',
  'Personal Assistant',
  'Team Assistant',
  'Enterprise Assistant',
];

const ASSISTANT_TYPE_ALIASES: Record<string, string> = {
  'Personal Agent': 'Personal Assistant',
  'Team Agent': 'Team Assistant',
  'Enterprise Agent': 'Enterprise Assistant',
};

const toCanonicalAiCategory = (value?: string) => {
  const normalized = normalizeTextValue(value);
  if (!normalized) {
    return undefined;
  }
  if (normalized === 'Co-pilot') {
    return 'Copilot';
  }
  return normalized;
};

const toAssistantTypeValue = (value?: string) => {
  const normalized = normalizeTextValue(value);
  if (!normalized) {
    return undefined;
  }
  if (!ASSISTANT_TYPE_VALUES.includes(normalized)) {
    return undefined;
  }
  return ASSISTANT_TYPE_ALIASES[normalized] || normalized;
};

const toAgentCard = (
  assistant: AssistantRecord,
  bookmarkIds: Set<string>,
): AgentCardProps => {
  const targetAudience = (assistant.targetAudience as any) || {};
  const governance = (assistant.governance as any) || {};
  const evidence = (assistant.evidence as any) || {};
  const integrationMethod = (assistant.integrationMethod as any) || {};
  const agentConfiguration = (assistant.agentConfiguration as any) || {};
  const interactionSettings = (assistant.interactionSettings as any) || {};
  const status = normalizeStatus(assistant.approvalStatus || assistant.status);
  const assistantId = assistant.id;
  const createdBy = assistant.createdBy || assistant.createdby;
  const ownerNameRaw = assistant.owner?.name;
  const normalizedOwnerName = ownerNameRaw?.trim();
  const ownerName =
    normalizedOwnerName && normalizedOwnerName.toLowerCase() !== 'unknown'
      ? normalizedOwnerName
      : createdBy || 'Unknown';
  const ownerEmail = assistant.owner?.email || createdBy;
  const useCases = (assistant.useCases || []).map(useCase => ({
    name: useCase,
    color: DEFAULT_BADGE_COLOR,
  }));

  return {
    id: assistantId,
    title: assistant.name || 'Untitled Assistant',
    description: assistant.description || '',
    avatarColor: assistant.avatar?.color || '#4A7BA7',
    useCases,
    agentType: assistant.assistantType || 'Personal Assistant',
    agentTypeColor: DEFAULT_BADGE_COLOR,
    owner: ownerName,
    ownerEmail,
    createdBy,
    segment: assistant.segment,
    aiCategory: assistant.aiCategory,
    approvalStatus: status,
    updatedTime: formatDate(
      assistant.updatedAt ||
        assistant.updatedat ||
        assistant.createdAt ||
        assistant.createdat,
    ),
    bookmarked:
      typeof assistant.bookmarked === 'boolean'
        ? assistant.bookmarked
        : !!assistantId && bookmarkIds.has(assistantId),
    modalContent: {
      overview: assistant.overview ? [assistant.overview] : assistant.description ? [assistant.description] : [],
      capabilities: assistant.capabilities || [],
      targetAudience: {
        designedFor:
          targetAudience.designedfor ||
          targetAudience.designedFor ||
          assistant.owner?.name ||
          '',
        businessGroup:
          targetAudience.businessgroup ||
          targetAudience.businessGroup ||
          assistant.lob ||
          '',
        geography: targetAudience.geography || '',
        geographicDetails:
          targetAudience.specificlocation || targetAudience.specificLocation,
        segment: assistant.segment,
        lob: assistant.lob,
        aiCategory: assistant.aiCategory,
      },
      whatYouProvide: assistant.whatYouProvide || [],
      whatYouGet: assistant.whatYouGet || [],
      governance: {
        dataAccess: governance.dataaccess || governance.dataAccess || '',
        compliance:
          governance.compliance ||
          (status === 'Approved' ? 'Approved' : 'Under Review'),
        validationSummary:
          governance.validationsummary ||
          governance.validationSummary ||
          assistant.comments ||
          '',
        validatedBy: governance.validatedby || governance.validatedBy || 'AI Governance',
        version: governance.version || 'v1.0',
        importantToKnow:
          governance.importanttoknow ||
          governance.importantToKnow ||
          'Validate assistant outputs before operational decisions.',
        evidenceLink: evidence.link,
        evidenceFileName: evidence.filename,
        evidenceFilePath: evidence.filepath,
        evidenceFileType: evidence.filetype,
        evidenceFileSize: evidence.filesize,
      },
      integrationMethod: {
        method: integrationMethod.method,
        configuration: integrationMethod.configuration,
      },
      agentConfiguration: {
        agentUrl: agentConfiguration.agentUrl,
        agentVersion: agentConfiguration.agentVersion,
        skills: agentConfiguration.skills || [],
        systemCapabilities: agentConfiguration.systemCapabilities || [],
      },
      interactionSettings: {
        inputFormat: interactionSettings.inputFormat,
        responseFormat: interactionSettings.responseFormat,
        requiresSecureAccess: !!interactionSettings.requiresSecureAccess,
      },
      releaseNotes: (assistant.releaseNotes || []).map(note => ({
        version: note.version,
        notes:
          note.changes && note.changes.length
            ? note.changes
            : [note.summary || 'No release notes details'],
      })),
    },
  };
};

const toUpsertPayload = (assistant: AgentCardProps) => {
  const ownerEmail = normalizeTextValue(assistant.ownerEmail);
  const modalContent = assistant.modalContent;
  const governance = modalContent?.governance;
  const useCases = (assistant.useCases || []).map(item => item.name);
  const integrationMethod = modalContent?.integrationMethod;
  const agentConfiguration = modalContent?.agentConfiguration;
  const interactionSettings = modalContent?.interactionSettings;
  const aiCategory = toCanonicalAiCategory(
    normalizeTextValue(modalContent?.targetAudience?.aiCategory) ||
      normalizeTextValue(assistant.aiCategory) ||
      normalizeTextValue(assistant.agentType),
  );
  const assistantType = toAssistantTypeValue(normalizeTextValue(assistant.agentType));
  const ownerType = /\bteam\b/i.test(assistant.owner || '') ? 'team' : 'individual';

  return {
    name: normalizeTextValue(assistant.title) || 'Untitled Assistant',
    description: normalizeTextValue(assistant.description) || '',
    segment: normalizeTextValue(modalContent?.targetAudience?.segment),
    aiCategory,
    assistantType,
    approvalStatus: 'Under Review',
    useCases: normalizeArrayValues(useCases),
    overview: normalizeTextValue(modalContent?.overview?.[0]),
    avatar: {
      init:
        assistant.title
          ?.split(' ')
          .filter(Boolean)
          .map(word => word[0])
          .join('')
          .slice(0, 2)
          .toUpperCase() || 'AI',
      color: normalizeTextValue(assistant.avatarColor),
    },
    owner: {
      name: normalizeTextValue(assistant.owner) || 'Unknown',
      email: ownerEmail,
      type: ownerType,
    },
    targetAudience: {
      designedfor: normalizeTextValue(modalContent?.targetAudience?.designedFor),
      businessgroup: normalizeTextValue(modalContent?.targetAudience?.businessGroup),
      geography: normalizeTextValue(modalContent?.targetAudience?.geography),
      specificlocation: normalizeTextValue(
        modalContent?.targetAudience?.geographicDetails,
      ),
    },
    governance: {
      dataaccess: normalizeTextValue(governance?.dataAccess),
      compliance: normalizeTextValue(governance?.compliance),
      validationsummary: normalizeTextValue(governance?.validationSummary),
      version: normalizeTextValue(governance?.version),
      validatedby: normalizeTextValue(governance?.validatedBy),
      importanttoknow: normalizeTextValue(governance?.importantToKnow),
    },
    evidence:
      normalizeTextValue(governance?.evidenceLink) ||
      normalizeTextValue(governance?.evidenceFileName) ||
      normalizeTextValue(governance?.evidenceFilePath)
        ? {
            link: normalizeTextValue(governance?.evidenceLink),
            filename: normalizeTextValue(governance?.evidenceFileName),
            filepath: normalizeTextValue(governance?.evidenceFilePath),
            filetype: normalizeTextValue(governance?.evidenceFileType),
            filesize: governance?.evidenceFileSize,
          }
        : undefined,
    capabilities: normalizeArrayValues(modalContent?.capabilities),
    whatYouProvide: normalizeArrayValues(modalContent?.whatYouProvide),
    whatYouGet: normalizeArrayValues(modalContent?.whatYouGet),
    integrationMethod:
      normalizeTextValue(integrationMethod?.method) ||
      normalizeTextValue(integrationMethod?.configuration)
        ? {
            method: normalizeTextValue(integrationMethod?.method),
            configuration: normalizeTextValue(integrationMethod?.configuration),
          }
        : undefined,
    agentConfiguration:
      normalizeTextValue(agentConfiguration?.agentUrl) ||
      normalizeTextValue(agentConfiguration?.agentVersion) ||
      (agentConfiguration?.skills || []).length ||
      (agentConfiguration?.systemCapabilities || []).length
        ? {
            agentUrl: normalizeTextValue(agentConfiguration?.agentUrl),
            agentVersion: normalizeTextValue(agentConfiguration?.agentVersion),
            skills: normalizeArrayValues(agentConfiguration?.skills),
            systemCapabilities: normalizeArrayValues(
              agentConfiguration?.systemCapabilities,
            ),
          }
        : undefined,
    interactionSettings:
      normalizeTextValue(interactionSettings?.inputFormat) ||
      normalizeTextValue(interactionSettings?.responseFormat) ||
      typeof interactionSettings?.requiresSecureAccess === 'boolean'
        ? {
            inputFormat: normalizeTextValue(interactionSettings?.inputFormat),
            responseFormat: normalizeTextValue(interactionSettings?.responseFormat),
            requiresSecureAccess: !!interactionSettings?.requiresSecureAccess,
          }
        : undefined,
  };
};

const getAssistantDisplayName = (assistant?: Partial<AgentCardProps> | null) =>
  assistant?.title?.trim() || 'Agent';

const AiAssistantsCatalog = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { assistantId } = useParams<{ assistantId?: string }>();
  const assistantsApi: AiAssistantsCatalogApi = useApi(aiAssistantsCatalogApiRef);
  const notificationApi: NotificationApi = useApi(notificationApiRef);
  const identityApi = useApi(identityApiRef);

  const isAddRoute = location.pathname.endsWith('/add');
  const isEditRoute = location.pathname.includes('/edit/');
  const isReviewRoute = location.pathname.includes('/review/');

  const [agents, setAgents] = useState<AgentCardProps[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('discover');
  const [selectedAgent, setSelectedAgent] = useState<AgentCardProps | null>(null);
  const [showAddAssistant, setShowAddAssistant] = useState(false);
  const [reviewingAgent, setReviewingAgent] = useState<AgentCardProps | null>(null);
  const [editingAgent, setEditingAgent] = useState<AgentCardProps | null>(null);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [filters, setFilters] = useState({
    assistantType: 'All',
    approvalStatus: 'All',
    useCase: 'All',
  });

  useEffect(() => {
    identityApi
      .getProfileInfo()
      .then(profile => {
        setCurrentUserEmail(normalizeEmailValue(profile?.email));
      })
      .catch(() => {
        setCurrentUserEmail('');
      });
  }, [identityApi]);

  const loadAssistants = useCallback(async () => {
    setLoading(true);
    try {
      const [assistantsResponse, bookmarksResponse] = await Promise.all([
        assistantsApi.getAllAssistants(),
        assistantsApi.getUserBookmarks(),
      ]);

      const bookmarks = extractBookmarkIds(bookmarksResponse);
      const mappedAgents = extractAssistantList(assistantsResponse).map(assistant =>
        toAgentCard(assistant, bookmarks),
      );

      setAgents(mappedAgents);
      setSelectedAgent(prev => {
        if (!prev) {
          return null;
        }
        return (
          mappedAgents.find(
            item => item.id === prev.id || item.title === prev.title,
          ) || prev
        );
      });
    } catch (error: any) {
      notificationApi.sendNotification({
        message: error?.message || 'Failed to load AI assistants',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [assistantsApi, notificationApi]);

  useEffect(() => {
    void loadAssistants();
  }, [loadAssistants]);

  useEffect(() => {
    if (isAddRoute) {
      setShowAddAssistant(true);
      setReviewingAgent(null);
      setEditingAgent(null);
      return;
    }

    if ((isEditRoute || isReviewRoute) && assistantId) {
      const openRouteAssistant = async () => {
        try {
          const detailResponse = await assistantsApi.getAssistantById(assistantId);
          const detailedAssistant = extractAssistantList(detailResponse)[0];

          if (!detailedAssistant) {
            throw new Error('Assistant details were not returned');
          }

          const mapped = toAgentCard(detailedAssistant, new Set<string>());
          if (isReviewRoute) {
            setReviewingAgent(mapped);
            setEditingAgent(null);
          } else {
            setEditingAgent(mapped);
            setReviewingAgent(null);
          }
          setShowAddAssistant(true);
        } catch (error: any) {
          notificationApi.sendNotification({
            message: error?.message || 'Failed to load agent details',
            severity: 'error',
          });
          navigate('/ai-store/agents', { replace: true });
        }
      };

      void openRouteAssistant();
      return;
    }

    setShowAddAssistant(false);
    setReviewingAgent(null);
    setEditingAgent(null);
  }, [assistantId, assistantsApi, isAddRoute, isEditRoute, isReviewRoute, navigate, notificationApi]);

  const getAssistantDetails = useCallback(
    async (assistant: AgentCardProps) => {
      if (!assistant.id) {
        return assistant;
      }

      const detailResponse = await assistantsApi.getAssistantById(assistant.id);
      const detailedAssistant = extractAssistantList(detailResponse)[0];

      if (!detailedAssistant) {
        throw new Error('Assistant details were not returned');
      }

      const fallbackBookmarks = new Set<string>();
      if (assistant.bookmarked) {
        fallbackBookmarks.add(assistant.id);
      }

      const mapped = toAgentCard(detailedAssistant, fallbackBookmarks);
      if (
        typeof detailedAssistant.bookmarked !== 'boolean' &&
        typeof assistant.bookmarked === 'boolean'
      ) {
        mapped.bookmarked = assistant.bookmarked;
      }

      return mapped;
    },
    [assistantsApi],
  );

  const filteredAgents = useMemo(() => {
    let results = agents.filter(
      agent =>
        agent.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.useCases.some(uc => uc.name.toLowerCase().includes(searchQuery.toLowerCase())),
    );

    if (activeTab === 'bookmarks') {
      results = results.filter(agent => agent.bookmarked);
    } else if (activeTab === 'items-for-review') {
      results = results.filter(agent => agent.approvalStatus === 'Under Review');
    } else if (activeTab === 'trending') {
      results = results.filter(agent => agent.approvalStatus === 'Approved');
    } else if (activeTab === 'recommended') {
      results = results.filter(agent => agent.agentType === 'Personal Agent');
    }

    if (filters.assistantType !== 'All') {
      results = results.filter(agent => agent.agentType === filters.assistantType);
    }
    if (filters.approvalStatus !== 'All') {
      results = results.filter(agent => agent.approvalStatus === filters.approvalStatus);
    }
    if (filters.useCase !== 'All') {
      results = results.filter(agent =>
        agent.useCases.some(uc => uc.name === filters.useCase),
      );
    }

    return results;
  }, [agents, searchQuery, activeTab, filters]);

  const handleBookmarkToggle = async (agent: AgentCardProps) => {
    if (!agent.id) {
      notificationApi.sendNotification({
        message: 'Agent id is missing for bookmark action',
        severity: 'error',
      });
      return;
    }

    const currentBookmarked = !!agent.bookmarked;

    setAgents(prev =>
      prev.map(item =>
        item.id === agent.id ? { ...item, bookmarked: !currentBookmarked } : item,
      ),
    );

    try {
      if (currentBookmarked) {
        await assistantsApi.removeBookmark(agent.id);
        notificationApi.sendNotification({
          message: `Removed "${getAssistantDisplayName(agent)}" from bookmarks`,
          severity: 'success',
        });
      } else {
        await assistantsApi.bookmarkAssistant(agent.id);
        notificationApi.sendNotification({
          message: `Added "${getAssistantDisplayName(agent)}" to bookmarks`,
          severity: 'success',
        });
      }
    } catch (error: any) {
      setAgents(prev =>
        prev.map(item =>
          item.id === agent.id ? { ...item, bookmarked: currentBookmarked } : item,
        ),
      );
      notificationApi.sendNotification({
        message: error?.message || 'Failed to update bookmark',
        severity: 'error',
      });
    }
  };

  const handleSaveAssistant = async (assistant: AgentCardProps) => {
    try {
      const targetAssistantId = assistant.id || reviewingAgent?.id || editingAgent?.id;
      const isExistingAssistant = !!targetAssistantId;
      const payload = toUpsertPayload(assistant);

      let resolvedId = targetAssistantId;

      if (targetAssistantId) {
        await assistantsApi.updateAssistant(targetAssistantId, payload);
      } else {
        const createResponse = await assistantsApi.createAssistant(payload);
        resolvedId = extractAssistantId(createResponse);
        if (resolvedId) {
          await assistantsApi.updateApprovalStatus(resolvedId, {
            status: assistant.approvalStatus,
            comments: '',
          });
        }
      }

      await loadAssistants();
      setShowAddAssistant(false);
      setReviewingAgent(null);
      setEditingAgent(null);
      setActiveTab('discover');
      navigate('/ai-store/agents');

      const assistantName = getAssistantDisplayName(assistant);
      let successMessage = `Saved "${assistantName}" successfully`;
      if (assistant.approvalStatus === 'Approved') {
        successMessage = isExistingAssistant
          ? `Approved and saved "${assistantName}"`
          : `Created and approved "${assistantName}"`;
      } else if (isExistingAssistant) {
        successMessage = `Updated "${assistantName}" and submitted for review`;
      } else {
        successMessage = `Submitted "${assistantName}" for review`;
      }

      notificationApi.sendNotification({
        message: successMessage,
        severity: 'success',
      });
    } catch (error: any) {
      notificationApi.sendNotification({
        message: error?.message || 'Failed to save agent',
        severity: 'error',
      });
    }
  };

  const handleRejectAssistant = async (assistant: AgentCardProps) => {
    const targetAssistantId = assistant.id || reviewingAgent?.id;
    const assistantName = getAssistantDisplayName(assistant);

    if (!targetAssistantId) {
      setAgents(prev => prev.filter(item => item.title !== assistant.title));
      setShowAddAssistant(false);
      setReviewingAgent(null);
      setEditingAgent(null);
      setActiveTab('items-for-review');
      navigate('/ai-store/agents');
      notificationApi.sendNotification({
        message: `Discarded draft "${assistantName}"`,
        severity: 'info',
      });
      return;
    }

    try {
      await assistantsApi.updateApprovalStatus(targetAssistantId, {
        status: 'Rejected',
        comments: '',
      });
      await loadAssistants();
      setShowAddAssistant(false);
      setReviewingAgent(null);
      setEditingAgent(null);
      setActiveTab('items-for-review');
      navigate('/ai-store/agents');
      notificationApi.sendNotification({
        message: `Rejected "${assistantName}"`,
        severity: 'success',
      });
    } catch (error: any) {
      notificationApi.sendNotification({
        message: error?.message || 'Failed to reject agent',
        severity: 'error',
      });
    }
  };

  const handleApproveAssistant = async (assistant: AgentCardProps) => {
    if (!assistant.id) {
      notificationApi.sendNotification({
        message: 'Agent id is missing for approve action',
        severity: 'error',
      });
      return;
    }

    try {
      await assistantsApi.updateApprovalStatus(assistant.id, {
        status: 'Approved',
        comments: '',
      });
      await loadAssistants();
      setShowAddAssistant(false);
      setReviewingAgent(null);
      setEditingAgent(null);
      setActiveTab('items-for-review');
      navigate('/ai-store/agents');
      notificationApi.sendNotification({
        message: `Approved "${getAssistantDisplayName(assistant)}"`,
        severity: 'success',
      });
    } catch (error: any) {
      notificationApi.sendNotification({
        message: error?.message || 'Failed to approve agent',
        severity: 'error',
      });
    }
  };

  const handleReviewAssistant = (assistant: AgentCardProps) => {
    if (!assistant.id) {
      notificationApi.sendNotification({
        message: 'Agent id is missing for review action',
        severity: 'error',
      });
      return;
    }

    navigate(`/ai-store/agents/review/${assistant.id}`);
  };

  const handleEditAssistant = async (assistant: AgentCardProps) => {
    if (!assistant.id) {
      notificationApi.sendNotification({
        message: 'Agent id is missing for edit action',
        severity: 'error',
      });
      return;
    }

    navigate(`/ai-store/agents/edit/${assistant.id}`);
  };

  const handleOpenAssistantDetails = async (assistant: AgentCardProps) => {
    try {
      const detailedAssistant = await getAssistantDetails(assistant);
      setSelectedAgent(detailedAssistant);
    } catch (error: any) {
      notificationApi.sendNotification({
        message: error?.message || 'Failed to load agent details',
        severity: 'error',
      });
    }
  };

  const extractRedirectUrl = (response: any): string | undefined => {
    if (!response) {
      return undefined;
    }

    if (typeof response?.data?.redirect_url === 'string') {
      return response.data.redirect_url;
    }

    if (typeof response?.redirect_url === 'string') {
      return response.redirect_url;
    }

    return undefined;
  };

  const getExternalAgentApiError = (response: any): string | undefined => {
    const code = response?.status?.code;
    if (typeof code === 'number' && code !== 0) {
      return (
        response?.status?.message ||
        response?.status?.desc ||
        'external-agents API returned a non-success status'
      );
    }

    return undefined;
  };

  const extractConnectionStringUrl = (
    agent: AgentCardProps,
    defaultUrl?: string,
  ): string | undefined => {
    if (defaultUrl) {
      return defaultUrl;
    }

    const candidates = [
      ...(agent.modalContent?.whatYouProvide || []),
      ...(agent.modalContent?.whatYouGet || []),
    ].map(value => value?.trim());

    return candidates.find(value => !!value && /^https?:\/\//i.test(value));
  };

  const handleUseAgent = async (agent: AgentCardProps, defaultUrl?: string) => {
    const notify = (
      message: string,
      severity: 'info' | 'success' | 'warning' | 'error',
    ) => {
      notificationApi.sendNotification({
        message,
        severity,
        disapperAfterMs: 2500,
      });
    };

    const isCopilot =
      isCopilotCategory(agent.aiCategory) ||
      isCopilotCategory(agent.modalContent?.targetAudience?.aiCategory);

    if (!isCopilot) {
      if (defaultUrl) {
        window.open(defaultUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      notify('Agent URL is not configured', 'warning');
      return;
    }

    const connectionStringUrl = extractConnectionStringUrl(agent, defaultUrl);
    if (!connectionStringUrl) {
      notify(
        'Copilot connection URL is required. Add a valid URL in the Input & Output section.',
        'warning',
      );
      return;
    }

    try {
      const response = await assistantsApi.createExternalAgent({
        provider: 'mcs',
        title: agent.title,
        description: agent.description,
        user_email: currentUserEmail || normalizeEmailValue(agent.ownerEmail),
        schema_id: '',
        environment_id: '',
        connection_string_url: connectionStringUrl,
      });

      const apiError = getExternalAgentApiError(response);
      if (apiError) {
        throw new Error(apiError);
      }

      const redirectUrl = extractRedirectUrl(response);
      if (!redirectUrl) {
        throw new Error('redirect_url was not returned by external-agents API');
      }

      window.open(redirectUrl, '_blank', 'noopener,noreferrer');
    } catch (error: any) {
      notify(error?.message || 'Failed to launch Copilot agent', 'error');
    }
  };

  return (
    <PageWithHeader
      title="AI Store"
      themeId="home"
      subtitle={
        <DevxBreadCrumb
          routes={[
            {
              type: 'link',
              link: '/',
              text: 'Home',
            },
            {
              type: 'link',
              link: '/ai-store',
              text: 'AI Store',
            },
            {
              type: 'text',
              link: '',
              text: 'Agents',
            },
          ]}
        />
      }
    >
      <Content>
        <div className={classes.buttonsWrapper}>
          <BackButton />
          <div className={classes.rightButtons}>
            <Button
              onClick={() => navigate('/ai-store/agents/guide')}
              role="button"
              color="primary"
              variant="text"
              className={classes.guideButton}
            >
              <DescriptionIcon /> Guide
            </Button>
          </div>
        </div>
        <div className={classes.content}>
          <div className={classes.container}>
            {showAddAssistant ? (
              <AddAssistant
                onCancel={() => {
                  navigate('/ai-store/agents');
                  notificationApi.sendNotification({
                    message: 'Closed agent form',
                    severity: 'info',
                  });
                }}
                onSave={handleSaveAssistant}
                onReject={handleRejectAssistant}
                onApprove={handleApproveAssistant}
                initialData={editingAgent || reviewingAgent || undefined}
                isReviewMode={!!reviewingAgent}
              />
            ) : (
              <>
            <FilterBar
              totalCount={agents.length}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              onFilterChange={(filterType, value) =>
                setFilters(prev => ({ ...prev, [filterType]: value }))
              }
            />

            <div className={classes.searchBarWrapper}>
              <div className={classes.searchBarLarge}>
                <input
                  type="text"
                  placeholder="Search Agents by name, Use case, Keywords, Creator name, Department, Team"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className={classes.searchInputLarge}
                />
                <div className={classes.searchButton}>
                  <SearchIcon width={20} height={20} />
                </div>
              </div>
              <button
                className={classes.addAgentBtn}
                onClick={() => {
                  navigate('/ai-store/agents/add');
                  notificationApi.sendNotification({
                    message: 'Opened new agent form',
                    severity: 'info',
                  });
                }}
              >
                + Add New Agent
              </button>
            </div>

            {loading && <div className={classes.emptyState}><p>Loading agents...</p></div>}

            {activeTab === 'items-for-review' ? (
              <ReviewTable
                agents={filteredAgents}
                onApprove={handleApproveAssistant}
                onReject={handleRejectAssistant}
                onReview={handleReviewAssistant}
              />
            ) : (
              <div className={classes.agentsGrid}>
                {filteredAgents.map(agent => (
                  (() => {
                    const ownerCandidateEmail = normalizeEmailValue(
                      agent.ownerEmail || agent.createdBy,
                    );
                    const canEdit =
                      !!currentUserEmail &&
                      !!ownerCandidateEmail &&
                      ownerCandidateEmail === currentUserEmail;
                    return (
                  <AgentCard
                    key={agent.id || agent.title}
                    {...agent}
                    canEdit={canEdit}
                    onEdit={() => {
                      void handleEditAssistant(agent);
                    }}
                    onBookmarkToggle={() => handleBookmarkToggle(agent)}
                    onClick={() => {
                      void handleOpenAssistantDetails(agent);
                    }}
                  />
                    );
                  })()
                ))}
              </div>
            )}

            {filteredAgents.length === 0 && (
              <div className={classes.emptyState}>
                <p>No agents found matching your criteria.</p>
              </div>
            )}
              </>
            )}
          </div>
        </div>

        {selectedAgent && (
          <AgentModal
            agent={selectedAgent}
            onClose={() => setSelectedAgent(null)}
            onBookmark={() => handleBookmarkToggle(selectedAgent)}
            onUseAgent={handleUseAgent}
          />
        )}
      </Content>
    </PageWithHeader>
  );
};

export default AiAssistantsCatalog;